<template>
  <div relative>
    <div relative min-h-lg>
      <MapContour
        v-if="userLocation || mapLoaded"
        :options="options"
        @load="loadMap"
        :sources="sources"
        :layers="layers"
        :marker="true"
        :show-labels="false"
        @markerMove="$emit('markerMove', $event)"
        class="bg-stone-100 bg-opacity-50"
      />
      <div v-else bg-stone-100 bg-opacity-50 absolute h-full w-full></div>
      <transition enter-from-class="translate-y-10 scale-80 opacity-0" enter-active-class="transition duration-300">
        <div
          class="my-2 absolute bottom-0 sm:bottom-12 left-0 m-2 p-2 px-4 rounded-3 bg-orange-50 bg-opacity-40 backdrop-blur-sm shadow shadow-neutral-400"
          v-if="mapLoaded && snowfall"
        >
          <div font-mono text-stone-700 font-600 lh-snug text-lg>{{ selected }} Snowfall</div>
          <p text-5xl sm:text-6xl font-bold>{{ snowfall }} in</p>
        </div>
      </transition>
    </div>
    <Toggle class="my-4 sm:(absolute bottom-0 left-0 m-2)" color="blue" v-model="selected" :options="layerOptions" />
    <p color-stone-500 sm:hidden v-if="selectedMeta?.endTime">
      <strong font-semibold>Date:</strong>
      {{ selectedMeta.startTime }} - {{ selectedMeta.endTime }}.<br />
      <strong font-semibold v-if="selectedMeta?.maxSnowfallInInches">Highest reported snowfall:</strong>
      {{ selectedMeta.maxSnowfallInInches }} inches.
    </p>
    <Legend class="my-4 sm:(absolute bottom-0 right-0 m-2)" :options="legend" />
  </div>
  <p class="color-stone-500 hidden sm:block" v-if="selectedMeta?.endTime">
    <strong font-semibold>Date:</strong>
    {{ selectedMeta.startTime }} - {{ selectedMeta.endTime }}.
    <strong font-semibold v-if="selectedMeta?.maxSnowfallInInches">Highest reported snowfall:</strong>
    {{ selectedMeta.maxSnowfallInInches }} inches.
  </p>
</template>
<script setup>
import { point } from "@turf/turf";
import Legend from "../Legend.vue";
import { useDateFormat, useNow } from "@vueuse/core";

const emit = defineEmits(["markerMove"]);
const props = defineProps({
  location: {
    type: Object,
  },
  options: {
    type: Object,
    default: {},
  },
});

let map;

const selected = ref("24h");
const mapLoaded = ref(false);
const snowfall = ref();

const layerOptions = [
  {
    value: "6h",
    label: "6h",
  },
  {
    value: "24h",
    label: "24h",
  },
  {
    value: "48h",
    label: "48h",
  },
  {
    value: "72h",
    label: "72h",
  },
];

const userLocation = computed(() => {
  if (isNumber(props.location?.latitude) && isNumber(props.location?.longitude)) {
    return point([props.location.longitude, props.location.latitude], {
      type: "userLocation",
    });
  }
  return null;
});

const cacheTimestamp = useDateFormat(useNow(), "YYYY-MM-DD-HH-mm");

function formatDate(date) {
  const day =
    date.getDate() == new Date().getDate()
      ? "Today"
      : date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return day + ", " + date.toLocaleTimeString(undefined, { hour: "numeric", minute: "numeric" });
}

function useSnowfallMetaFetch(type) {
  return useLazyFetch(`latest_${type}.json`, {
    baseURL: "https://tiles.whatismyelevation.com/snowfall",
    query: {
      _: cacheTimestamp,
    },
    transform: (data) => {
      const startDate = new Date(data?.startTime * 1000);
      const startTime = formatDate(startDate);

      const endDate = new Date(data?.endTime * 1000);
      let endTime =
        endDate.getDate() !== startDate.getDate()
          ? formatDate(endDate)
          : endDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "numeric" });

      return {
        maxSnowfallInInches: round(data?.maxSnowfallInMeters * 39.3701),
        startTime,
        endTime,
      };
    },
  });
}

const mapMetaFetch = await Promise.all(layerOptions.map((layer) => useSnowfallMetaFetch(layer.value)));

const mapMeta = computed(() => {
  return {
    "6h": mapMetaFetch[0]?.data.value,
    "24h": mapMetaFetch[1]?.data.value,
    "48h": mapMetaFetch[2]?.data.value,
    "72h": mapMetaFetch[3]?.data.value,
  };
});
const selectedMeta = computed(() => mapMeta.value[selected.value]);

const legend = computed(() => {
  const maxSnowfall = selectedMeta.value?.maxSnowfallInInches;
  if (maxSnowfall > 0) {
    // get the inches number from the name string and only show the legend items that are less than the max snowfall
    return SNOWFALL_MAP_LEGEND.filter((item) => {
      const inches = parseInt(item.name.match(/\d+/)[0]);
      return inches <= maxSnowfall;
    });
  }
  return SNOWFALL_MAP_LEGEND;
});

const sources = computed(() => {
  return [
    {
      id: "snowfall",
      type: "vector",
      url: `pmtiles://https://tiles.whatismyelevation.com/snowfall/latest_${selected.value}.pmtiles?v=${cacheTimestamp.value}`,
    },
  ];
});

const options = computed(() => ({
  center: userLocation.value.geometry.coordinates,
  fitBoundsOptions: { padding: 50 },
  zoom: 9,
  style: "/styles/radar.json",
  ...props.options,
}));

const getNumberFromLegend = (legendName) => {
  const match = legendName.match(/(\d+)/);
  if (legendName.startsWith("<")) return 0;
  if (legendName.startsWith(">")) return 121;
  return match ? parseInt(match[0]) : 0;
};

const getColorForSnowDepth = (inches) => {
  const matchingLegend = SNOWFALL_MAP_LEGEND.findLast((item) => getNumberFromLegend(item.name) <= inches);
  return matchingLegend?.color ?? SNOWFALL_MAP_LEGEND[0].color;
};

const layers = [
  ...Array.from({ length: 122 }, (_, i) => {
    return {
      id: `snowfall-${i}`,
      source: "snowfall",
      "source-layer": "snowfall",
      type: "fill",
      paint: {
        "fill-color": getColorForSnowDepth(i),
        "fill-opacity": {
          stops: [
            [0, 0.6],
            [12, i < 2 ? 0.5 : 0.4],
            [16, i < 2 ? 0.4 : 0.2],
          ],
        },
      },
      filter: ["==", "ID", i],
      beforeId: "label_airport",
    };
  }),
  {
    id: "contour-text",
    type: "symbol",
    source: "snowfall",
    "source-layer": "snowfall",
    filter: [">", ["get", "min_inches"], 0],
    paint: {
      "text-color": "rgb(107, 115, 124)",
      "text-halo-color": "rgba(255, 255, 255, 0.75)",
      "text-halo-width": 1,
    },
    layout: {
      "symbol-placement": "line",
      "text-size": 10,
      "text-field": ["concat", ["number-format", ["get", "min_inches"], {}], " in"],
      "text-font": ["Noto Sans Bold"],
    },
  },
];

watch(
  () => props.location,
  () => {
    querySnowfall();
  },
);

function querySnowfall() {
  if (!map || !userLocation.value || !map.getBounds().contains(userLocation.value.geometry.coordinates)) {
    return;
  }
  // if the map is too zoomed out it will be inaccurate
  if (map.getZoom() < 5) {
    return;
  }
  const center = map.project(userLocation.value.geometry.coordinates);
  const features = map.queryRenderedFeatures(center).filter((feature) => feature.source === "snowfall");
  if (!features.length) {
    snowfall.value = "0";
    return;
  }
  const minInches = features[0]?.properties?.min_inches;
  if (minInches < 1) {
    snowfall.value = "< 1";
  } else {
    snowfall.value = new Intl.NumberFormat().format(minInches);
  }
}

function loadMap(mapValue) {
  map = mapValue;
  mapLoaded.value = true;
  map.on("data", () => {
    querySnowfall();
  });
  map.on("idle", () => {
    querySnowfall();
  });
}
</script>
